/**
 * Judgments API — court outcomes.
 */
import { api } from './api';

export interface Judgment {
  id: string;
  citation: string;
  case: string;
  caseId: string | null;
  /** The court's own claim/case number (distinct from a neutral citation). */
  claimNumber: string | null;
  court: string;
  judge: string;
  date: string;
  barrister: string;
  area: string;
  outcome: string;
  pages: number;
  summary: string;
  keyPoints: string;
  tags: string[];
  isPublic: boolean;
  /** The uploaded sealed court order, if one has been attached. */
  orderDocument: { id: string; name: string; size: string } | null;
}

interface ApiJudgment {
  id: string;
  caseName: string;
  neutralCitation: string | null;
  judgmentDate: string;
  outcome: string;
  summary: string | null;
  keyPoints: string | null;
  tags: string[];
  isPublic: boolean;
  pdfSizeBytes: string | null;
  createdAt: string;
  case: { id: string; caseNumber: string; courtCaseNumber: string | null; caseName: string } | null;
  court: { id: string; name: string; shortName: string } | null;
  judge: { id: string; title: string; fullName: string; shortName: string } | null;
  practiceArea: { id: string; name: string } | null;
  linkedBarrister: { id: string; user: { firstName: string; lastName: string; memberType: string } } | null;
  orderDocument: { id: string; name: string; fileSizeBytes: string | number; mimeType: string } | null;
}

function outcomeLabel(o: string): string {
  return o.charAt(0).toUpperCase() + o.slice(1);
}

function formatSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function apiToJudgment(j: ApiJudgment): Judgment {
  const barUser = j.linkedBarrister?.user;
  const barName = barUser ? `${barUser.firstName} ${barUser.lastName}${barUser.memberType === 'silk' ? ' KC' : ''}` : '';

  return {
    id: j.id,
    citation: j.neutralCitation ?? '',
    case: j.caseName,
    caseId: j.case?.id ?? null,
    claimNumber: j.case?.courtCaseNumber ?? null,
    court: j.court?.shortName ?? '',
    judge: j.judge?.shortName ?? j.judge?.fullName ?? '',
    date: new Date(j.judgmentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    barrister: barName,
    area: j.practiceArea?.name ?? '',
    outcome: outcomeLabel(j.outcome),
    pages: 0,
    summary: j.summary ?? '',
    keyPoints: j.keyPoints ?? '',
    tags: j.tags,
    isPublic: j.isPublic,
    orderDocument: j.orderDocument
      ? {
          id: j.orderDocument.id,
          name: j.orderDocument.name,
          size: formatSize(
            typeof j.orderDocument.fileSizeBytes === 'string'
              ? parseInt(j.orderDocument.fileSizeBytes, 10)
              : j.orderDocument.fileSizeBytes,
          ),
        }
      : null,
  };
}

export async function fetchJudgments(opts?: { search?: string }): Promise<Judgment[]> {
  const res = await api.get('/judgments', {
    params: { limit: 100, sortBy: 'judgmentDate', sortOrder: 'desc', ...(opts?.search && { search: opts.search }) },
  });
  const payload = res.data?.data ?? res.data;
  const judgments: ApiJudgment[] = payload.judgments ?? [];
  return judgments.map(apiToJudgment);
}

export async function fetchJudgment(id: string): Promise<Judgment> {
  const res = await api.get(`/judgments/${id}`);
  const j = (res.data as any).data ?? res.data;
  return apiToJudgment(j as ApiJudgment);
}

export interface CreateJudgmentPayload {
  caseName: string;
  caseId?: string;
  neutralCitation?: string;
  courtId?: string;
  judgeId?: string;
  judgmentDate: string;
  outcome: 'won' | 'lost' | 'partial' | 'settled' | 'dismissed' | 'adjourned' | 'withdrawn';
  practiceAreaId?: string;
  linkedBarristerId?: string;
  summary?: string;
  keyPoints?: string;
  isPublic?: boolean;
  tags?: string[];
  /** Id of an already-uploaded court-order Document to link to this judgment. */
  orderDocumentId?: string;
}

export async function createJudgment(payload: CreateJudgmentPayload): Promise<Judgment> {
  const res = await api.post('/judgments', payload);
  const j = (res.data as any).data ?? res.data;
  return apiToJudgment(j as ApiJudgment);
}

/**
 * Attach (or, with null, detach) the sealed court-order document on an existing judgment —
 * used when the order arrives after the outcome was first recorded.
 */
export async function setJudgmentOrderDocument(id: string, orderDocumentId: string | null): Promise<Judgment> {
  const res = await api.patch(`/judgments/${id}`, { orderDocumentId });
  const j = (res.data as any).data ?? res.data;
  return apiToJudgment(j as ApiJudgment);
}

export async function deleteJudgment(id: string): Promise<void> {
  await api.delete(`/judgments/${id}`);
}
